import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Trash2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/atoms/alert';
import { Button } from '@/components/atoms/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import {
  makeDefaultRule,
  PaymentScheduleRulesEditor,
} from '@/components/organisms/payment-schedules/PaymentScheduleRulesEditor';
import {
  scheduleRuleToUnifiedRule,
  validateDraftEstimatePaymentSchedule,
  type DraftEstimatePaymentSchedule,
} from '@/components/organisms/payment-schedules/estimateTemplateValidation';
import type { UnifiedRule } from '@/hooks/payment-schedules/usePaymentScheduleEditor';
import {
  useSchedulePresets,
  type PaymentSchedulePreset,
  type ScheduleRule,
} from '@/hooks/payment-schedule-templates/usePaymentScheduleTemplate';
import { useEstimateSettings } from '@/hooks/settings/useEstimateSettings';

interface NewEstimatePaymentScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: DraftEstimatePaymentSchedule | null;
  contactId: string | null;
  grandTotal: number;
  depositRequired: boolean;
  /** Dollars the deposit clause collects — counts against the plan's 100%. */
  depositAmount: number;
  onSave: (value: DraftEstimatePaymentSchedule) => void;
  onRemove: () => void;
  description?: string;
  /** Proposals require an explicit plan choice per option, without a tenant preset. */
  useDefaultPreset?: boolean;
}

function toScheduleRule(rule: UnifiedRule): ScheduleRule {
  return {
    trigger: rule.trigger,
    action: rule.action,
    amountSpec: {
      ...rule.amountSpec,
      daysAfter: rule.daysAfter ?? rule.amountSpec.daysAfter,
      fixedDate: rule.fixedDate ?? rule.amountSpec.fixedDate,
    },
    autoCharge: rule.action === 'AUTO_CHARGE',
    milestoneRef: rule.milestoneRef ?? undefined,
  };
}

function rulesFromPreset(preset: PaymentSchedulePreset): UnifiedRule[] {
  return preset.rules.map(scheduleRuleToUnifiedRule);
}

export function NewEstimatePaymentScheduleDialog({
  open,
  onOpenChange,
  value,
  contactId,
  grandTotal,
  depositRequired,
  depositAmount,
  onSave,
  onRemove,
  description = 'Configure the stages now. The estimate and schedule are not created until you save the estimate.',
  useDefaultPreset = true,
}: NewEstimatePaymentScheduleDialogProps) {
  const [rules, setRules] = useState<UnifiedRule[]>([makeDefaultRule()]);
  const [previewText, setPreviewText] = useState('');
  const initializedForOpenRef = useRef(false);
  const settingsQuery = useEstimateSettings({ enabled: open && !value && useDefaultPreset });
  const presetsQuery = useSchedulePresets({ enabled: open && !value && useDefaultPreset });
  const defaultPresetId = settingsQuery.data?.paymentSchedule?.defaultPresetId;

  useEffect(() => {
    if (!open) {
      initializedForOpenRef.current = false;
      return;
    }
    if (initializedForOpenRef.current) return;
    if (!value && useDefaultPreset && (settingsQuery.isLoading || presetsQuery.isLoading)) return;
    const defaultPreset =
      useDefaultPreset && defaultPresetId
        ? presetsQuery.data?.find((preset) => preset.id === defaultPresetId)
        : undefined;
    setRules(
      value?.rules.map(scheduleRuleToUnifiedRule) ??
        (defaultPreset ? rulesFromPreset(defaultPreset) : [makeDefaultRule()]),
    );
    setPreviewText(value?.previewText ?? '');
    initializedForOpenRef.current = true;
  }, [
    open,
    value,
    defaultPresetId,
    useDefaultPreset,
    settingsQuery.isLoading,
    presetsQuery.data,
    presetsQuery.isLoading,
  ]);

  const errors = useMemo(
    () =>
      validateDraftEstimatePaymentSchedule(
        { rules: rules.map(toScheduleRule), previewText },
        grandTotal,
        { required: depositRequired, amount: depositAmount },
      ),
    [rules, previewText, grandTotal, depositRequired, depositAmount],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Set up payment schedule</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {errors.length > 0 ? (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertDescription>
                <ul className="list-disc space-y-0.5 pl-4">
                  {errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          ) : null}

          <PaymentScheduleRulesEditor
            contactId={contactId}
            rules={rules}
            onRulesChange={(nextRules) => {
              initializedForOpenRef.current = true;
              setRules(nextRules);
            }}
            grandTotal={grandTotal}
            jobScheduledStart={null}
            jobScheduledEnd={null}
            jobCompletedAt={null}
            inset
          />

          <div className="space-y-1.5">
            <Label htmlFor="new-estimate-payment-schedule-summary">
              Customer-facing summary <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="new-estimate-payment-schedule-summary"
              value={previewText}
              onChange={(event) => {
                initializedForOpenRef.current = true;
                setPreviewText(event.target.value);
              }}
              placeholder="e.g. 25% deposit, balance on completion"
            />
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          {value ? (
            <Button
              type="button"
              variant="ghost"
              className="text-destructive"
              onClick={() => {
                onRemove();
                onOpenChange(false);
              }}
            >
              <Trash2 className="size-4" />
              Remove schedule
            </Button>
          ) : (
            <span />
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={errors.length > 0}
              onClick={() => {
                onSave({
                  rules: rules.map(toScheduleRule),
                  ...(previewText.trim() ? { previewText: previewText.trim() } : {}),
                });
                onOpenChange(false);
              }}
            >
              Apply schedule
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
